import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Grid, Group, Modal, Select, TextInput, Title } from "@mantine/core";
import { ClientUser, ClientUserRole } from "src/graphql/generated/graphql";
import { ClientLocale } from "src/types";

import { useAuthContext } from "../../hooks/auth";
import { useInviteClientUser } from "../../hooks/clients";
import { createModal } from "../../hooks/modals";

export interface InviteClientUserModalProps {
    onClose();
    onSave?(clientUser: ClientUser);
}

export const InviteClientUserModal = createModal(({ onClose, onSave }: InviteClientUserModalProps) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const allowedRoles = useMemo(() => {
        const allowedRoles: ClientUserRole[] = [];

        switch (client?.role) {
            case ClientUserRole.Owner:
                allowedRoles.push(ClientUserRole.Owner);
            case ClientUserRole.Admin:
                allowedRoles.push(ClientUserRole.Admin);
                allowedRoles.push(ClientUserRole.Developer);
                allowedRoles.push(ClientUserRole.Member);
                break;
            case ClientUserRole.Developer:
            case ClientUserRole.Member:
                break;
        }

        return allowedRoles;
    }, [client?.role]);

    const [email, setEmail] = useState<string>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [role, setRole] = useState<ClientUserRole>(ClientUserRole.Admin);
    const [lang, setLang] = useState<ClientLocale>(ClientLocale.en);

    const { inviteClientUser } = useInviteClientUser(client.id);

    const handleSubmit = useCallback(async () => {
        const clientUser = await inviteClientUser(
            {
                email,
                firstName,
                lastName,
            },
            role,
            lang
        );

        onSave?.(clientUser);
        onClose?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, onClose, email, firstName, lastName]);

    return (
        <Modal
            size="lg"
            title={<Title order={2}>{t("client_users_invite")}</Title>}
            styles={{
                header: { backgroundColor: "transparent" },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                content: { overflowY: "visible !important" as any },
            }}
            onClose={onClose}
            opened
        >
            <TextInput
                mb="xs"
                label={t("email")}
                placeholder={t("email")}
                value={email}
                onChange={(event) => setEmail(String(event?.target?.value).toLowerCase())}
                withAsterisk
            />

            <Grid>
                <Grid.Col xs={12} md={6}>
                    <Select
                        mb="xs"
                        label={t("clientuser_role")}
                        placeholder={t("clientuser_role")}
                        data={allowedRoles.map((value) => ({
                            value,
                            label: t(`clientuser_role_${value}`),
                        }))}
                        value={role}
                        onChange={(role: ClientUserRole) => setRole(role)}
                        withAsterisk
                    />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <Select
                        mb="xs"
                        label={t("clientuser_lang")}
                        placeholder={t("clientuser_lang")}
                        data={Object.values(ClientLocale).map((value) => ({
                            value,
                            label: t(`clientuser_lang_${value}`),
                        }))}
                        value={lang}
                        onChange={(lang: ClientLocale) => setLang(lang)}
                        withAsterisk
                    />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <TextInput
                        label={t("firstname")}
                        placeholder={t("firstname")}
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        withAsterisk
                    />
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <TextInput
                        label={t("lastname")}
                        placeholder={t("lastname")}
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        withAsterisk
                    />
                </Grid.Col>
            </Grid>
            <Group mt="lg" position="right">
                <Button onClick={() => handleSubmit()}>{t("client_users_invite")}</Button>
            </Group>
        </Modal>
    );
});
